import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, MessagesSquare, User, Bot, Flag, FlagOff,
  BookOpen, AlertTriangle, ExternalLink, Loader2, ShieldAlert,
} from "lucide-react";
import api from "../../lib/api";

/**
 * Brief 3.6 Phase A1 — Admin transcript reader (detail view).
 *
 * Per-assistant-turn surface:
 *   • Confidence pill (derived from citation count + abstain flag)
 *   • Knowledge source attribution (TWEAK 2): single source title,
 *     "general brain (no curated source)" warning, or multi-source list
 *   • Flag-for-review button + optional note (TWEAK 1)
 */

const CONFIDENCE_PILL = {
  high:      { dot: "bg-emerald-400", chip: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30", label: "High" },
  medium:    { dot: "bg-amber-400",   chip: "bg-amber-400/10 text-amber-300 border-amber-400/30",     label: "Medium" },
  low:       { dot: "bg-rose-400",    chip: "bg-rose-400/10 text-rose-300 border-rose-400/30",         label: "Low" },
  abstained: { dot: "bg-zinc-500",    chip: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",         label: "Abstained" },
};

const SOURCE_TONE = {
  abstained:     { icon: ShieldAlert,    chip: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30" },
  general_brain: { icon: AlertTriangle,  chip: "bg-rose-500/10 text-rose-300 border-rose-400/30" },
  single_source: { icon: BookOpen,       chip: "bg-cyan-500/10 text-cyan-300 border-cyan-400/30" },
  multi_source:  { icon: BookOpen,       chip: "bg-emerald-500/10 text-emerald-300 border-emerald-400/30" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ConfidenceChip({ value, testId }) {
  const cfg = CONFIDENCE_PILL[value] || CONFIDENCE_PILL.low;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-md border ${cfg.chip}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function KnowledgeSourcePanel({ source, messageId }) {
  const [open, setOpen] = useState(false);
  if (!source) return null;
  const tone = SOURCE_TONE[source.kind] || SOURCE_TONE.single_source;
  const Icon = tone.icon;
  const hasCitations = (source.citations || []).length > 0;
  return (
    <div
      data-testid={`message-source-${messageId}`}
      className={`mt-2 rounded-lg border px-2.5 py-1.5 text-[12px] ${tone.chip}`}
    >
      <button
        type="button"
        onClick={() => hasCitations && setOpen((o) => !o)}
        className={`w-full flex items-center gap-1.5 ${hasCitations ? "cursor-pointer" : "cursor-default"}`}
        data-testid={`message-source-toggle-${messageId}`}
      >
        <Icon className="h-3.5 w-3.5" />
        <span className="flex-1 text-left">{source.label}</span>
        {hasCitations && (
          <span className="text-[10.5px] opacity-70">
            {open ? "Hide sources" : `Show ${source.citations.length} source${source.citations.length === 1 ? "" : "s"}`}
          </span>
        )}
      </button>
      {open && hasCitations && (
        <ul className="mt-2 space-y-1.5 pl-5" data-testid={`message-source-list-${messageId}`}>
          {source.citations.map((c, i) => (
            <li key={`${c.chunk_id}-${i}`} className="text-[11.5px] flex items-start gap-2">
              <span className="text-zinc-500 mt-0.5">[{c.source}]</span>
              <div className="flex-1">
                <div className="text-zinc-200">{c.source_title || c.chunk_id || "—"}</div>
                {c.source_url && (
                  <a
                    href={c.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:underline inline-flex items-center gap-1 mt-0.5"
                  >
                    {c.source_url} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FlagButton({ message, threadId, onFlagged, onUnflagged }) {
  const existingFlag = (message.flags || []).find((f) => f.source === "admin");
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(existingFlag?.note || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit() {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        `/admin/nerea/conversations/${threadId}/messages/${message.message_id}/flag`,
        { note: note || null },
      );
      onFlagged(message.message_id, {
        flag_id: data.flag_id,
        note: data.note,
        source: "admin",
      });
      setOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to flag message.");
    } finally {
      setBusy(false);
    }
  }

  async function unflag() {
    if (!existingFlag) return;
    setBusy(true);
    setErr("");
    try {
      await api.delete(`/admin/nerea/conversations/flags/${existingFlag.flag_id}`);
      onUnflagged(message.message_id, existingFlag.flag_id);
      setOpen(false);
      setNote("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to remove flag.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-[11.5px] px-2 py-1 rounded-md border transition-colors ${
          existingFlag
            ? "bg-amber-400/10 border-amber-400/30 text-amber-300 hover:bg-amber-400/20"
            : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05]"
        }`}
        data-testid={`message-flag-button-${message.message_id}`}
      >
        {existingFlag ? <Flag className="h-3 w-3 fill-current" /> : <Flag className="h-3 w-3" />}
        {existingFlag ? "Flagged" : "Mark for review"}
      </button>
    );
  }

  return (
    <div
      className="mt-2 rounded-lg border border-white/[0.08] bg-white/[0.02] p-2.5"
      data-testid={`message-flag-editor-${message.message_id}`}
    >
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 500))}
        placeholder="What's wrong with this answer? (optional, 500 chars max)"
        rows={2}
        className="w-full bg-transparent text-[12.5px] text-zinc-200 placeholder-zinc-600 outline-none resize-none"
        data-testid={`message-flag-note-${message.message_id}`}
      />
      {err && (
        <div className="text-[11px] text-rose-300 mt-1" data-testid={`message-flag-error-${message.message_id}`}>
          {err}
        </div>
      )}
      <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
        <span>{500 - note.length} chars left</span>
        <div className="flex items-center gap-2">
          {existingFlag && (
            <button
              onClick={unflag}
              disabled={busy}
              className="inline-flex items-center gap-1 text-rose-300 hover:text-rose-200 disabled:opacity-50"
              data-testid={`message-flag-remove-${message.message_id}`}
            >
              <FlagOff className="h-3 w-3" />
              Remove flag
            </button>
          )}
          <button
            onClick={() => { setOpen(false); setNote(existingFlag?.note || ""); setErr(""); }}
            disabled={busy}
            className="text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
            data-testid={`message-flag-cancel-${message.message_id}`}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-400/15 text-amber-200 border border-amber-400/30 hover:bg-amber-400/25 disabled:opacity-50"
            data-testid={`message-flag-save-${message.message_id}`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Flag className="h-3 w-3" />}
            {existingFlag ? "Update note" : "Flag this turn"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message, threadId, onFlagged, onUnflagged }) {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";
  const Icon = isUser ? User : Bot;
  const rowRef = useRef(null);

  // Auto-scroll to the hash-targeted message on first paint.
  useEffect(() => {
    if (typeof window !== "undefined" &&
        window.location.hash === `#msg-${message.message_id}`) {
      rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [message.message_id]);

  return (
    <div
      id={`msg-${message.message_id}`}
      ref={rowRef}
      className={`flex gap-3 ${isUser ? "" : ""}`}
      data-testid={`message-${message.message_id}`}
    >
      <div className={`h-7 w-7 rounded-full flex items-center justify-center border ${
        isUser ? "bg-white/[0.05] border-white/[0.08] text-zinc-300"
              : "bg-cyan-500/10 border-cyan-400/30 text-cyan-300"
      }`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[11px] text-zinc-500 mb-1">
          <span className={isUser ? "text-zinc-400" : "text-cyan-300"}>
            {isUser ? "Merchant" : "Nerea"}
          </span>
          <span>·</span>
          <span title={message.created_at}>{fmtDateTime(message.created_at)}</span>
          {isAssistant && message.confidence && (
            <>
              <span>·</span>
              <ConfidenceChip
                value={message.confidence}
                testId={`message-confidence-${message.message_id}`}
              />
            </>
          )}
          {isAssistant && message.charter_version && (
            <>
              <span>·</span>
              <span
                className="text-[10.5px] text-zinc-500 font-mono"
                title="Nerea Operating Charter version that governed this turn"
                data-testid={`message-charter-${message.message_id}`}
              >
                charter {message.charter_version}
              </span>
            </>
          )}
          {isAssistant && message.abstained && message.abstention_reason && (
            <>
              <span>·</span>
              <span className="text-rose-300" data-testid={`message-abstention-${message.message_id}`}>
                {message.abstention_reason}
              </span>
            </>
          )}
          {isAssistant && message.redacted && (
            <span className="text-amber-300" title="Output filter redacted PII before display">
              · redacted
            </span>
          )}
        </div>
        <div
          className={`rounded-xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap break-words ${
            isUser
              ? "bg-white/[0.03] border border-white/[0.06] text-zinc-200"
              : "bg-cyan-500/[0.04] border border-cyan-400/15 text-zinc-100"
          }`}
          data-testid={`message-content-${message.message_id}`}
        >
          {message.content || <span className="text-zinc-500 italic">— empty —</span>}
        </div>

        {isAssistant && (
          <KnowledgeSourcePanel
            source={message.knowledge_source}
            messageId={message.message_id}
          />
        )}

        {isAssistant && (
          <div className="mt-2">
            <FlagButton
              message={message}
              threadId={threadId}
              onFlagged={onFlagged}
              onUnflagged={onUnflagged}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminNereaConversationDetail() {
  const { threadId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data: payload } = await api.get(`/admin/nerea/conversations/${threadId}`);
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load transcript.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [threadId]);

  const summary = useMemo(() => {
    const msgs = data?.messages || [];
    const asst = msgs.filter((m) => m.role === "assistant");
    const counts = { high: 0, medium: 0, low: 0, abstained: 0 };
    asst.forEach((m) => {
      const c = m.confidence || "low";
      counts[c] = (counts[c] || 0) + 1;
    });
    return { total: msgs.length, assistant_total: asst.length, counts };
  }, [data]);

  function handleFlagged(messageId, flag) {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: prev.messages.map((m) => {
          if (m.message_id !== messageId) return m;
          const otherFlags = (m.flags || []).filter((f) => f.source !== "admin");
          return { ...m, flags: [...otherFlags, flag] };
        }),
      };
    });
  }

  function handleUnflagged(messageId, flagId) {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: prev.messages.map((m) => {
          if (m.message_id !== messageId) return m;
          return { ...m, flags: (m.flags || []).filter((f) => f.flag_id !== flagId) };
        }),
      };
    });
  }

  return (
    <div className="max-w-4xl" data-testid="admin-nerea-conversation-detail">
      <Link
        to="/admin/nerea/conversations"
        className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-400 hover:text-[#00E5FF]"
        data-testid="conversation-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to conversations
      </Link>

      <div className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
        <MessagesSquare className="h-3 w-3 text-[#00E5FF]" /> Transcript
      </div>

      {loading && (
        <div data-testid="detail-loading" className="mt-6 text-[13px] text-zinc-500">
          Loading transcript…
        </div>
      )}

      {err && (
        <div data-testid="detail-error" className="mt-6 rounded-lg border border-rose-400/30 bg-rose-500/[0.05] px-3 py-2 text-[12.5px] text-rose-200">
          {err}
        </div>
      )}

      {data && (
        <>
          <h1
            className="font-display mt-2 text-2xl sm:text-3xl font-semibold tracking-tighter text-white"
            data-testid="detail-title"
          >
            {data.thread.title}
          </h1>
          <div className="mt-2 text-[13px] text-zinc-400">
            <Link
              to={`/admin/merchants/${data.thread.merchant_id}`}
              className="text-zinc-200 hover:text-[#00E5FF]"
              data-testid="detail-merchant-link"
            >
              {data.thread.merchant_label || data.thread.merchant_id || "Unknown merchant"}
            </Link>
            <span className="mx-2 text-zinc-600">·</span>
            <span title={data.thread.created_at}>
              Started {fmtDateTime(data.thread.created_at)}
            </span>
            <span className="mx-2 text-zinc-600">·</span>
            <span title={data.thread.last_message_at}>
              Last message {fmtDateTime(data.thread.last_message_at)}
            </span>
          </div>

          {/* Per-thread confidence summary tiles */}
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-2.5" data-testid="detail-summary">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] px-3 py-2.5">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Assistant turns</div>
              <div className="mt-1 text-[18px] text-zinc-200">{summary.assistant_total}</div>
            </div>
            {(["high", "medium", "low", "abstained"]).map((k) => (
              <div
                key={k}
                className="rounded-xl border border-white/[0.06] bg-white/[0.015] px-3 py-2.5"
                data-testid={`detail-summary-${k}`}
              >
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{k}</div>
                <div className={`mt-1 text-[18px] ${
                  k === "high"      ? "text-emerald-300" :
                  k === "medium"    ? "text-amber-300" :
                  k === "low"       ? "text-rose-300" :
                                       "text-zinc-400"
                }`}>{summary.counts[k] || 0}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 space-y-6" data-testid="message-list">
            {data.messages.length === 0 ? (
              <div className="text-[13px] text-zinc-500 text-center py-10">
                — empty thread —
              </div>
            ) : (
              data.messages.map((m) => (
                <MessageBubble
                  key={m.message_id}
                  message={m}
                  threadId={data.thread.thread_id}
                  onFlagged={handleFlagged}
                  onUnflagged={handleUnflagged}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
